import { c as _c } from "react/compiler-runtime";
import { Typography } from '@mui/material';
import { type Condition, ConditionOperatorEnum } from '@repo/api-gw-sdk';
import { useMemo, useState, type UIEventHandler } from 'react';
import { AddButton } from '@/components/buttons/addButton';
import { SelectionPanel } from '@/components/panels/selectionPanel/selectionPanel';
import { ScrollableContent } from '@/components/scroll/scrollableContent';
import { ScrollableWrapper } from '@/components/scroll/scrollableContext';
import { ScrollableHeader } from '@/components/scroll/scrollableHeader';
import { useScrollable } from '@/components/scroll/useScrollable';
import { createCombineCondition, findValueCondition, type FilterOption } from '@/types/advanceFilter';
import { pluralize } from '@/utils/string';
import { tagEntityLabel } from './tagKeyValueUtils';
import { TagSearchBoxes } from './tagSearchBoxes';
import type { TagsDataResult, TagsFilterProps } from './tagsFilter';
import { useResourceTagsData, useSourceAccountTagsData } from './tagsFilter';
import { useTagOptions } from './useTagOptions';
import { defaultOperatorFor, MultiSelectFilter } from '../filters';
import { CLOUD_PROVIDER_PROPERTY } from '../propertyNames';

/** Keeps the operator the condition already carries; only a brand-new condition gets the default. */
const withSelectedTags = (props: TagsFilterProps, values: string[]): Condition[] => {
  const existingCondition = findValueCondition(props.conditions, props.property.name);
  return props.conditions.filter(x => x !== existingCondition).concat(values.length ? [{
    property: props.property.name,
    operator: existingCondition?.operator ?? defaultOperatorFor(props.property),
    value: values
  }] : []);
};
function useProviderFilter(props) {
  const $ = _c(2);
  let t0;
  bb0: {
    if (!props.supportedProviders?.length) {
      t0 = undefined;
      break bb0;
    }
    let t1;
    if ($[0] !== props.supportedProviders) {
      t1 = createCombineCondition([{
        property: CLOUD_PROVIDER_PROPERTY,
        operator: ConditionOperatorEnum.In,
        value: props.supportedProviders
      }]);
      $[0] = props.supportedProviders;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    t0 = t1;
  }
  return t0;
}
const ResourceTagsConditionWrapper = props => {
  const $ = _c(3);
  const filter = useProviderFilter(props);
  const tagsData = useResourceTagsData(filter);
  let t0;
  if ($[0] !== props || $[1] !== tagsData) {
    t0 = <TagsConditionContent {...props} tagsData={tagsData} />;
    $[0] = props;
    $[1] = tagsData;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};
const SourceAccountTagsConditionWrapper = props => {
  const $ = _c(3);
  const filter = useProviderFilter(props);
  const tagsData = useSourceAccountTagsData(filter);
  let t0;
  if ($[0] !== props || $[1] !== tagsData) {
    t0 = <TagsConditionContent {...props} tagsData={tagsData} />;
    $[0] = props;
    $[1] = tagsData;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};
export const TagsCondition = props => {
  const $ = _c(4);
  if (props.dataSource === "sourceAccountTags") {
    let t0;
    if ($[0] !== props) {
      t0 = <SourceAccountTagsConditionWrapper {...props} />;
      $[0] = props;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    return t0;
  }
  let t0;
  if ($[2] !== props) {
    t0 = <ResourceTagsConditionWrapper {...props} />;
    $[2] = props;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return t0;
};
const TagsConditionContent = props => {
  const $ = _c(35);
  const [keyFilter, setKeyFilter] = useState("");
  const [valueFilter, setValueFilter] = useState("");
  const {
    data: body,
    isLoading
  } = props.tagsData;
  let t0;
  if ($[0] !== props.conditions || $[1] !== props.property.name) {
    t0 = findValueCondition(props.conditions, props.property.name);
    $[0] = props.conditions;
    $[1] = props.property.name;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const condition = t0;
  let t1;
  if ($[3] !== condition?.value) {
    t1 = condition?.value ?? [];
    $[3] = condition?.value;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const selectedResources = t1;
  const t2 = body?.tags;
  let t3;
  if ($[5] !== keyFilter || $[6] !== props.initialFilterMode || $[7] !== selectedResources || $[8] !== t2 || $[9] !== valueFilter) {
    t3 = {
      tags: t2,
      selectedValues: selectedResources,
      keyFilter,
      valueFilter,
      mode: props.initialFilterMode
    };
    $[5] = keyFilter;
    $[6] = props.initialFilterMode;
    $[7] = selectedResources;
    $[8] = t2;
    $[9] = valueFilter;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const options = useTagOptions(t3);
  let t4;
  if ($[11] !== props.initialFilterMode) {
    t4 = tagEntityLabel(props.initialFilterMode);
    $[11] = props.initialFilterMode;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const entityLabel = t4;
  let t5;
  if ($[13] !== props || $[14] !== selectedResources) {
    t5 = item => {
      props.onChange(withSelectedTags(props, selectedResources.filter(x => x !== item)));
    };
    $[13] = props;
    $[14] = selectedResources;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== condition || $[17] !== props) {
    t6 = () => props.onChange(props.conditions.filter(x_0 => x_0 !== condition));
    $[16] = condition;
    $[17] = props;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== entityLabel || $[20] !== selectedResources || $[21] !== t5 || $[22] !== t6) {
    t7 = {
      items: selectedResources,
      entityType: entityLabel,
      onRemove: t5,
      renderItem: _temp,
      itemKey: _temp2,
      clearAll: t6
    };
    $[19] = entityLabel;
    $[20] = selectedResources;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== entityLabel || $[25] !== keyFilter || $[26] !== options || $[27] !== props || $[28] !== selectedResources || $[29] !== valueFilter) {
    t8 = <ScrollableWrapper><TagsConditionBody {...props} options={options} entityLabel={entityLabel} selectedResources={selectedResources} keyFilter={keyFilter} valueFilter={valueFilter} setKeyFilter={setKeyFilter} setValueFilter={setValueFilter} /></ScrollableWrapper>;
    $[24] = entityLabel;
    $[25] = keyFilter;
    $[26] = options;
    $[27] = props;
    $[28] = selectedResources;
    $[29] = valueFilter;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  let t9;
  if ($[31] !== isLoading || $[32] !== t7 || $[33] !== t8) {
    t9 = <SelectionPanel isLoading={isLoading} drawerProps={t7}>{t8}</SelectionPanel>;
    $[31] = isLoading;
    $[32] = t7;
    $[33] = t8;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  return t9;
};
const TagsConditionBody = props => {
  const $ = _c(33);
  const {
    isScrolledVertically,
    setIsScrolledVertically
  } = useScrollable();
  let t0;
  if ($[0] !== isScrolledVertically || $[1] !== setIsScrolledVertically) {
    t0 = event => {
      const isScrolled = event.currentTarget.scrollTop > 0;
      if (isScrolled !== isScrolledVertically) {
        setIsScrolledVertically(isScrolled);
      }
    };
    $[0] = isScrolledVertically;
    $[1] = setIsScrolledVertically;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const onScroll = t0;
  const filteredValue = props.initialFilterMode === "key" ? props.keyFilter : `${props.keyFilter}=${props.valueFilter}`;
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      gap: "12px"
    };
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== props.entityLabel) {
    t2 = pluralize(props.entityLabel);
    $[4] = props.entityLabel;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = `Select one or more ${t2}`;
  let t4;
  if ($[6] !== t3) {
    t4 = <Typography className="my-[12px]" variant="body1">{t3}</Typography>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== filteredValue || $[9] !== props) {
    t5 = !props.panelHost && <AddButton disabled={props.selectedResources.includes(filteredValue) || !props.keyFilter} onClick={() => {
      props.onChange(withSelectedTags(props, [...props.selectedResources, filteredValue]));
    }} />;
    $[8] = filteredValue;
    $[9] = props;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== props.initialFilterMode || $[12] !== props.keyFilter || $[13] !== props.setKeyFilter || $[14] !== props.setValueFilter || $[15] !== props.valueFilter || $[16] !== t5) {
    t6 = <TagSearchBoxes mode={props.initialFilterMode} keyFilter={props.keyFilter} valueFilter={props.valueFilter} onKeyFilterChange={props.setKeyFilter} onValueFilterChange={props.setValueFilter} action={t5} />;
    $[11] = props.initialFilterMode;
    $[12] = props.keyFilter;
    $[13] = props.setKeyFilter;
    $[14] = props.setValueFilter;
    $[15] = props.valueFilter;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t4 || $[19] !== t6) {
    t7 = <ScrollableHeader hideBorderWhenOnTop={true} className="px-[24px] pb-[24px] py-[12px]" sx={t1}>{t4}{t6}</ScrollableHeader>;
    $[18] = t4;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== props.options.length || $[22] !== props.panelHost) {
    t8 = props.options.length === 0 && <Typography variant="body1" className="my-[12px]">{props.panelHost ? "No matching values found. Try a new search term." : "No matching values found. Try a new search term, or add this value manually to include it once it\u2019s discovered."}</Typography>;
    $[21] = props.options.length;
    $[22] = props.panelHost;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== onScroll || $[25] !== props) {
    t9 = props.options.length > 0 && <MultiSelectFilter {...props} data-testid={`filter-${props.property.name}`} showSelectAll={!!props.keyFilter || !!props.valueFilter} onVirtuosoScroll={onScroll} virtuosoStyles={{
      height: props.selectedResources.length ? props.initialFilterMode === "key-value" ? "226px" : "270px" : props.initialFilterMode === "key-value" ? "286px" : "330px",
      marginRight: "-24px",
      paddingRight: "24px"
    }} />;
    $[24] = onScroll;
    $[25] = props;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== t8 || $[28] !== t9) {
    t10 = <ScrollableContent className="px-[24px] py-[12px]">{t8}{t9}</ScrollableContent>;
    $[27] = t8;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== t10 || $[31] !== t7) {
    t11 = <>{t7}{t10}</>;
    $[30] = t10;
    $[31] = t7;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  return t11;
};
function _temp(item_0) {
  return <Typography>{item_0}</Typography>;
}
function _temp2(item_1) {
  return item_1;
}